import type {
  DeploymentUnitResourceItemBffV2Api,
  DeploymentUnitResourceReadBffV2Api,
  EnumDeploymentUnitStatusV2Api,
  PlaceholderKvApi,
  StatusDetailApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import type { DeploymentTargetDeviceResourceById } from '@ingenico/react-ui';
import { toString } from 'lodash-es';
import { computeConfigurationProfile, computeType } from '../../utils/deployment.util';

function computeDeploymentUnitStatusLabel(status: EnumDeploymentUnitStatusV2Api): string {
  switch (status) {
    case 'CANCELED': {
      return 'status.canceled';
    }
    case 'FAILED': {
      return 'status.failed';
    }
    case 'COMPLETED': {
      return 'status.completed';
    }
    case 'SCHEDULED': {
      return 'status.scheduled';
    }
    case 'WAITING_ATTACHMENT': {
      return 'status.waitingAttachment';
    }
    case 'CREATED': {
      return 'status.created';
    }
    case 'PAUSED': {
      return 'status.paused';
    }
    case 'RUNNING': {
      return 'status.running';
    }
    case 'QUEUED': {
      return 'status.queued';
    }
    default: {
      return 'status.running';
    }
  }
}

export function computeDeploymentUnitStatusColor(status?: EnumDeploymentUnitStatusV2Api): string {
  switch (status) {
    case 'FAILED': {
      return 'failed';
    }
    case 'CREATED':
    case 'COMPLETED': {
      return 'success';
    }

    case 'RUNNING':
    case 'QUEUED': {
      return 'info';
    }
    case 'SCHEDULED':
    case 'WAITING_ATTACHMENT':
    case 'PAUSED': {
      return 'warning';
    }
    case 'CANCELED': {
      return 'neutral';
    }

    default: {
      return 'info';
    }
  }
}

function manageSmartFieldPlaceholders(
  existingPlaceholders: PlaceholderKvApi[],
  key: string,
  value: string,
): PlaceholderKvApi[] {
  if (key === 'smartFieldId') {
    const sanitizedPlaceholders = existingPlaceholders.filter(
      ({ key: existingKey }) => existingKey !== 'smartFieldName',
    );
    return [...sanitizedPlaceholders, { key, value }];
  } else if (key === 'smartFieldName') {
    const hasSmartFieldIdKey = existingPlaceholders.some(({ key: existingKey }) => existingKey === 'smartFieldId');
    return [...existingPlaceholders, ...(hasSmartFieldIdKey ? [] : [{ key: 'smartFieldId', value }])];
  } else {
    return [...existingPlaceholders, { key, value }];
  }
}

function managePlaceholders(errorCode?: string, placeholders?: PlaceholderKvApi[]): PlaceholderKvApi[] | undefined {
  const placeholdersMap =
    placeholders?.reduce((accum, { key, value }) => ({ ...accum, [key]: value }), {} as Record<string, string>) ?? {};
  return errorCode === 'DPB-ERR-007'
    ? Object.entries(placeholdersMap)?.reduce(
        (accum, [key, value]) => manageSmartFieldPlaceholders(accum, key, value),
        [] as PlaceholderKvApi[],
      )
    : placeholders;
}

function computeDeploymentUnitStatusDetail(statusDetail: StatusDetailApi | undefined): StatusDetailApi | undefined {
  return statusDetail
    ? {
        ...statusDetail,
        ...('placeholders' in statusDetail
          ? { placeholders: managePlaceholders(statusDetail.errorCode, statusDetail.placeholders) }
          : {}),
      }
    : undefined;
}

export function mapDeploymentUnitResponse(
  response: DeploymentUnitResourceItemBffV2Api,
): DeploymentTargetDeviceResourceById {
  return {
    options: {
      configuration: response.options?.configuration
        ? {
            ...response.options?.configuration,
            snapshotId: response.options?.configuration.snapshotId,
            type: response.options?.configuration?.type,
            custom: response.options?.configuration?.custom
              ? {
                  ...response.options.configuration.custom,
                  definition: response.options.configuration.custom.definition,
                  description: response.options.configuration.custom.description,
                  name: toString(response.options.configuration.custom.name),
                }
              : undefined,
            reference: response.options.configuration?.reference,
          }
        : undefined,
      delete: response.options?.delete,
    },
    creationDate: response.creationDate,
    deploymentRequestId: response.deploymentRequestId,
    description: response.description,
    deviceId: response.deviceId,
    endDate: response.endDate,
    id: response.id,
    name: response.name,
    startDate: response.startDate,
    status: response.status,
    statusDetail: computeDeploymentUnitStatusDetail(response.statusDetail),
    logicalDevice: response.logicalDevice,
    merchantId: response.merchantId,
    merchantName: response.merchantName,
    physicalDevice: {
      ...response.physicalDevice,
      isBlocked: response.physicalDevice?.isBlocked ?? false,
    },
    computedType: computeType(response.options),
    computedConfigurationProfile: computeConfigurationProfile(response.options),
    computedStatusLabel: computeDeploymentUnitStatusLabel(response.status),
    computedStatusColor: computeDeploymentUnitStatusColor(response.status),
  };
}

export function mapDeploymentUnitResponseReadBFF(
  response: DeploymentUnitResourceReadBffV2Api,
): DeploymentTargetDeviceResourceById {
  return {
    options: {
      configuration: response.options?.configuration
        ? {
            ...response.options?.configuration,
            snapshotId: response.options?.configuration.snapshotId,
            type: response.options?.configuration?.type,
            custom: response.options?.configuration?.custom
              ? {
                  ...response.options.configuration.custom,
                  definition: response.options.configuration.custom.definition,
                  description: response.options.configuration.custom.description,
                  name: toString(response.options.configuration.custom.name),
                }
              : undefined,
            reference: response.options.configuration?.reference,
          }
        : undefined,
      delete: response.options?.delete,
    },
    creationDate: response.creationDate,
    deploymentRequestId: response.deploymentRequestId,
    description: response.description,
    deviceId: response.deviceId,
    endDate: response.endDate,
    id: response.id,
    name: response.name,
    startDate: response.startDate,
    status: response.status,
    statusDetail: computeDeploymentUnitStatusDetail(response.statusDetail),
    logicalDevice: response.logicalDevice,
    merchantId: response.merchantId,
    merchantName: response.merchantName,
    physicalDevice: {
      ...response.physicalDevice,
      isBlocked: response.physicalDevice?.isBlocked ?? false,
    },
    computedType: computeType(response.options),
    computedConfigurationProfile: computeConfigurationProfile(response.options),
    computedStatusLabel: computeDeploymentUnitStatusLabel(response.status),
    computedStatusColor: computeDeploymentUnitStatusColor(response.status),
  };
}
